import { Provider, createStore } from 'jotai';
import React, { ReactNode, useEffect, useState } from 'react';

import { seedStoreIdentity, subscribeStoreReset } from './storeReset';

/**
 * Provides the Jotai store for the whole layout and resets it wholesale on
 * company change.
 *
 * Jotai v2 has no "reset all atoms" API. Isolation is structural: on each
 * request we create a brand-new store AND key-remount <Provider>, so every
 * in-memory atom value and all component state below is discarded. The new
 * store is seeded with the identity chosen at the switch site so its first
 * frame already carries the new company/dashboard; the remaining
 * localStorage-backed atoms rehydrate from storage via their own onMount.
 */
export const JotaiStoreProvider = ({ children }: { children: ReactNode }) => {
  const [store, setStore] = useState(() => createStore());
  const [storeKey, setStoreKey] = useState(0);

  useEffect(
    () =>
      subscribeStoreReset((identity) => {
        // A NEW store per key is required — reusing a store with a new key does
        // NOT reset atom values.
        const nextStore = createStore();
        // Seed the new store with the identity chosen at the switch site so
        // its FIRST frame already carries the new company/dashboard (no -1 /
        // default-CA frame, which would fire spurious requests before onMount
        // rehydrates).
        seedStoreIdentity(nextStore, identity);
        // key change forces a full remount of everything under this Provider.
        setStore(nextStore);
        setStoreKey((k) => k + 1);
      }),
    [],
  );

  return (
    <Provider key={storeKey} store={store}>
      {children}
    </Provider>
  );
};
